/**
 * State + thunks for the Creator Settings → Notifications tab.
 *
 * Talks to /api/creator/notification-prefs via creatorNotificationsAPI;
 * server is the authoritative gate (self-scoped — derives identity from
 * the trusted SDK session, no userId in URL).
 *
 * Same shape as OrgSettingsNotificationsPage.duck, but without orgId in
 * the action payloads — a creator manages their own prefs.
 */

import { creatorNotificationsAPI } from '../../api';
import { storableError } from '../../util/errors';

// ================ Action types ================ //

export const FETCH_PREFS_REQUEST = 'app/CreatorSettingsNotificationsPage/FETCH_PREFS_REQUEST';
export const FETCH_PREFS_SUCCESS = 'app/CreatorSettingsNotificationsPage/FETCH_PREFS_SUCCESS';
export const FETCH_PREFS_ERROR = 'app/CreatorSettingsNotificationsPage/FETCH_PREFS_ERROR';

export const UPDATE_PREF_REQUEST = 'app/CreatorSettingsNotificationsPage/UPDATE_PREF_REQUEST';
export const UPDATE_PREF_SUCCESS = 'app/CreatorSettingsNotificationsPage/UPDATE_PREF_SUCCESS';
export const UPDATE_PREF_ERROR = 'app/CreatorSettingsNotificationsPage/UPDATE_PREF_ERROR';

// ================ Reducer ================ //

const initialState = {
  prefs: null,
  fetchInProgress: false,
  fetchLoaded: false,
  fetchError: null,
  savingKey: null,
  updateError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_PREFS_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_PREFS_SUCCESS:
      return { ...state, fetchInProgress: false, fetchLoaded: true, prefs: payload.prefs };
    case FETCH_PREFS_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    case UPDATE_PREF_REQUEST:
      // Optimistic flip — confirmed on success, rolled back on error.
      return {
        ...state,
        savingKey: payload.key,
        updateError: null,
        prefs: state.prefs ? { ...state.prefs, [payload.key]: payload.value } : state.prefs,
      };
    case UPDATE_PREF_SUCCESS:
      return {
        ...state,
        savingKey: null,
        prefs: payload.prefs || state.prefs,
      };
    case UPDATE_PREF_ERROR:
      return {
        ...state,
        savingKey: null,
        updateError: payload.error,
        prefs: state.prefs
          ? { ...state.prefs, [payload.key]: payload.previousValue }
          : state.prefs,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchPrefsRequest = () => ({ type: FETCH_PREFS_REQUEST });
const fetchPrefsSuccess = prefs => ({ type: FETCH_PREFS_SUCCESS, payload: { prefs } });
const fetchPrefsError = e => ({ type: FETCH_PREFS_ERROR, payload: e });

const updatePrefRequest = (key, value) => ({
  type: UPDATE_PREF_REQUEST,
  payload: { key, value },
});
const updatePrefSuccess = prefs => ({ type: UPDATE_PREF_SUCCESS, payload: { prefs } });
const updatePrefError = (key, previousValue, error) => ({
  type: UPDATE_PREF_ERROR,
  payload: { key, previousValue, error },
});

// ================ Thunks ================ //

export const fetchNotificationPrefs = () => async dispatch => {
  dispatch(fetchPrefsRequest());
  try {
    const response = await creatorNotificationsAPI.getPrefs();
    const body = response?.data ?? response ?? {};
    const data = body?.data ?? body;
    dispatch(fetchPrefsSuccess(data?.prefs || {}));
    return data?.prefs;
  } catch (e) {
    dispatch(fetchPrefsError(storableError(e)));
    throw e;
  }
};

export const updateNotificationPref = (key, value, previousValue) => async dispatch => {
  dispatch(updatePrefRequest(key, value));
  try {
    const response = await creatorNotificationsAPI.updatePrefs({ [key]: value });
    const body = response?.data ?? response ?? {};
    const data = body?.data ?? body;
    dispatch(updatePrefSuccess(data?.prefs));
    return data?.prefs;
  } catch (e) {
    dispatch(updatePrefError(key, previousValue, storableError(e)));
    throw e;
  }
};

// ================ loadData ================ //

export const loadData = () => dispatch => dispatch(fetchNotificationPrefs());
